import AsyncStorage from '@react-native-async-storage/async-storage';
import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { midnightTheme, themes, type ThemeName, type TonyteTheme } from './themes';
import { isThemeName, resolveThemeName } from './themeAccess';
import { applySeasonalTheme, type PublishedSeasonalTheme } from './seasonalTheme';
import { seasonalThemeStore } from './seasonalRuntime';
import { useReducedMotionPreference } from '../hooks/useReducedMotionPreference';

const THEME_STORAGE_KEY = '@tonyte/theme-preference';
const SEASONAL_ENABLED_KEY = '@tonyte/seasonal-appearance-enabled';
const SEASONAL_ANIMATIONS_KEY = '@tonyte/seasonal-animations-enabled';

interface ThemeContextValue {
  theme: TonyteTheme;
  themeName: ThemeName;
  canChangeTheme: boolean;
  setThemeName: (name: ThemeName) => Promise<void>;
  seasonalTheme: PublishedSeasonalTheme | null;
  seasonalAppearanceEnabled: boolean;
  seasonalAnimationsEnabled: boolean;
  setSeasonalAppearanceEnabled: (enabled: boolean) => Promise<void>;
  setSeasonalAnimationsEnabled: (enabled: boolean) => Promise<void>;
}

export const ThemeContext = createContext<ThemeContextValue>({
  theme: midnightTheme,
  themeName: 'midnight',
  canChangeTheme: true,
  setThemeName: async () => undefined,
  seasonalTheme: null,
  seasonalAppearanceEnabled: true,
  seasonalAnimationsEnabled: true,
  setSeasonalAppearanceEnabled: async () => undefined,
  setSeasonalAnimationsEnabled: async () => undefined,
});

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [preference, setPreference] = useState<ThemeName>('midnight');
  const [hydrated, setHydrated] = useState(false);
  const [seasonalAppearanceEnabled, setSeasonalAppearance] = useState(true);
  const [seasonalAnimationsEnabled, setSeasonalAnimations] = useState(true);
  const [remoteSeasonalTheme, setRemoteSeasonalTheme] = useState<PublishedSeasonalTheme | null>(null);
  const reduceMotion = useReducedMotionPreference();

  useEffect(() => {
    let active = true;
    Promise.all([
      AsyncStorage.getItem(THEME_STORAGE_KEY),
      AsyncStorage.getItem(SEASONAL_ENABLED_KEY),
      AsyncStorage.getItem(SEASONAL_ANIMATIONS_KEY),
    ])
      .then(([storedTheme, storedSeasonal, storedAnimations]) => {
        if (!active) return;
        if (isThemeName(storedTheme)) setPreference(storedTheme);
        if (storedSeasonal !== null) setSeasonalAppearance(storedSeasonal !== 'false');
        if (storedAnimations !== null) setSeasonalAnimations(storedAnimations !== 'false');
      })
      .catch(() => undefined)
      .finally(() => {
        if (active) setHydrated(true);
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const unsubscribe = seasonalThemeStore.subscribe(state => setRemoteSeasonalTheme(state.effective));
    seasonalThemeStore.start();
    setRemoteSeasonalTheme(seasonalThemeStore.getState().effective);
    return () => { unsubscribe(); seasonalThemeStore.stop(); };
  }, []);

  const setThemeName = useCallback(async (name: ThemeName) => {
    setPreference(name);
    try {
      await AsyncStorage.setItem(THEME_STORAGE_KEY, name);
    } catch (error) {
      console.warn('[Tonyte][Theme] could not persist appearance preference', error);
    }
  }, []);

  const setSeasonalAppearanceEnabled = useCallback(async (enabled: boolean) => {
    setSeasonalAppearance(enabled);
    await AsyncStorage.setItem(SEASONAL_ENABLED_KEY, String(enabled)).catch(() => undefined);
  }, []);

  const setSeasonalAnimationsEnabled = useCallback(async (enabled: boolean) => {
    setSeasonalAnimations(enabled);
    await AsyncStorage.setItem(SEASONAL_ANIMATIONS_KEY, String(enabled)).catch(() => undefined);
  }, []);

  const themeName = resolveThemeName(preference);
  const seasonalTheme = seasonalAppearanceEnabled ? remoteSeasonalTheme : null;
  const theme = useMemo(() => applySeasonalTheme(themes[themeName], seasonalTheme), [seasonalTheme, themeName]);
  const value = useMemo<ThemeContextValue>(() => ({
    theme,
    themeName,
    canChangeTheme: true,
    setThemeName,
    seasonalTheme,
    seasonalAppearanceEnabled,
    seasonalAnimationsEnabled: seasonalAnimationsEnabled && !reduceMotion,
    setSeasonalAppearanceEnabled,
    setSeasonalAnimationsEnabled,
  }), [reduceMotion, seasonalAnimationsEnabled, seasonalAppearanceEnabled, seasonalTheme, setSeasonalAnimationsEnabled, setSeasonalAppearanceEnabled, setThemeName, theme, themeName]);

  // Do not mount navigation with the Midnight fallback while a persisted
  // Daylight preference is still being restored. This keeps app entry and
  // cold starts on one continuous appearance with no opposite-theme frame.
  if (!hydrated) return null;

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
